'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/components/AuthProvider';
import { isProUser, getToken } from '@/lib/auth';
import { track } from '@/lib/analytics';
import {
  TAGLINE, TRIAL_DAYS, PRICE_ANNUAL, PRICE_MONTHLY,
  FREE_FEATURES, PRO_HEADLINE_FEATURES, PRO_MORE_FEATURES,
  readPreferredPlan, rememberPreferredPlan, rememberSignupIntent,
} from '@/lib/proFeatures';

// ── Icons ─────────────────────────────────────────────────────────────────────

function CheckIcon({ green = false }: { green?: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
      stroke={green ? '#2D5016' : 'currentColor'}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.5 7L5.5 11L12.5 3" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
      <path d="M2.5 4.5L6 8L9.5 4.5" />
    </svg>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PricingPage() {
  const { user, loading } = useAuth();
  const [plan, setPlan]         = useState<'annual' | 'monthly'>('annual');
  const [starting, setStarting] = useState(false);
  const [error, setError]       = useState('');
  const [showAll, setShowAll]   = useState(false);

  const isPro = user ? isProUser(user) : false;

  // Read from location rather than useSearchParams so the page needs no
  // Suspense boundary just to record where the visit came from.
  // Honour a plan chosen on the landing page before the visitor had an account.
  useEffect(() => {
    const preferred = readPreferredPlan();
    if (preferred) setPlan(preferred);
  }, []);

  useEffect(() => {
    const source = new URLSearchParams(window.location.search).get('source');
    track('pricing_page_viewed', {
      variant: source === 'prompt' || source === 'nav' ? source : 'direct',
    });
  }, []);

  async function startCheckout() {
    track('checkout_started', { variant: plan });
    setError('');
    setStarting(true);
    try {
      const token = getToken();
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ plan }),
      });
      const data = await res.json() as { url?: string; error?: string };
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setError(data.error ?? 'Could not start checkout.');
    } catch {
      setError('Could not start checkout.');
    }
    setStarting(false);
  }

  // ── Pro CTA ───────────────────────────────────────────────────────────────

  function ProCTA() {
    if (loading) return null;

    if (!user) {
      return (
        <Link href="/register"
          onClick={() => rememberSignupIntent('trial')}
          className="block w-full bg-deep-green text-white font-body font-bold text-[15px]
                     py-3 rounded-xl text-center hover:opacity-90 transition-opacity">
          Create your account and start the trial
        </Link>
      );
    }

    if (isPro) {
      return (
        <div className="w-full bg-light-sage text-deep-green font-body font-bold text-[15px]
                        py-3 rounded-xl text-center flex items-center justify-center gap-2">
          <CheckIcon green /> You&rsquo;re on Pro
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <button
          onClick={startCheckout}
          disabled={starting}
          className="w-full bg-deep-green text-white font-body font-bold text-[15px]
                     py-3 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {starting ? 'Opening checkout …' : `Start your ${TRIAL_DAYS}-day free trial`}
        </button>
        <p className="font-body text-[12px] text-sage text-center">
          {plan === 'annual'
            ? `Free for ${TRIAL_DAYS} days, then $49.99 a year`
            : `Free for ${TRIAL_DAYS} days, then $5.99 a month`}
        </p>
        {error && (
          <p className="font-body text-[13px] text-alert-red text-center">{error}</p>
        )}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-3xl space-y-8">

      <div className="text-center space-y-2">
        <h1 className="font-heading text-[32px] text-dark-text">Garden Pro</h1>
        <p className="font-body text-[16px] text-sage max-w-md mx-auto leading-relaxed">
          {TAGLINE}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

        {/* Free */}
        <div className="bg-white rounded-2xl border border-sand p-7 flex flex-col gap-6">
          <div>
            <h2 className="font-heading text-[22px] text-dark-text">Garden</h2>
            <div className="mt-3">
              <span className="font-heading text-[36px] text-dark-text leading-none">Free</span>
            </div>
          </div>

          <ul className="space-y-3 flex-1">
            {FREE_FEATURES.map(f => (
              <li key={f} className="flex items-start gap-2.5 font-body text-[14px] text-dark-text">
                <span className="text-sage mt-0.5 flex-shrink-0"><CheckIcon /></span>
                {f}
              </li>
            ))}
          </ul>

          {!loading && user && !isPro ? (
            <div className="w-full bg-light-sage text-sage font-body font-semibold text-[14px]
                            py-3 rounded-xl text-center">
              Current Plan
            </div>
          ) : (
            <Link href={user ? '/dashboard' : '/register'}
              className="block w-full font-body font-semibold text-[14px] text-sage py-3
                         rounded-xl text-center border border-sand hover:border-sage
                         transition-colors">
              {user ? 'Go to Dashboard' : 'Get Started Free'}
            </Link>
          )}
        </div>

        {/* Pro */}
        <div className="relative bg-white rounded-2xl border-2 border-deep-green p-7
                        flex flex-col gap-6">
          <div>
            <h2 className="font-heading text-[22px] text-dark-text">Garden Pro</h2>

            {/* Annual leads, but the displayed price always matches what will
                actually be charged. */}
            <div className="mt-3 flex items-end gap-2 flex-wrap">
              <span className="font-heading text-[36px] text-dark-text leading-none">
                {plan === 'annual' ? PRICE_ANNUAL : PRICE_MONTHLY}
              </span>
              <span className="font-body text-[14px] text-sage mb-0.5">
                / {plan === 'annual' ? 'year' : 'month'}
              </span>
              {plan === 'annual' && (
                <span className="font-body text-[11px] font-bold text-sage bg-light-sage
                                 border border-[#C5D9B8] px-2 py-0.5 rounded-full mb-0.5">
                  Two months free
                </span>
              )}
            </div>

            <button
              onClick={() => { const next = plan === 'annual' ? 'monthly' : 'annual'; setPlan(next); rememberPreferredPlan(next); }}
              className="mt-2 font-body text-[13px] text-deep-green underline
                         underline-offset-2 hover:opacity-70 transition-opacity text-left"
            >
              {plan === 'annual'
                ? 'or pay monthly at $5.99'
                : 'or pay yearly at $49.99 and get two months free'}
            </button>
          </div>

          <ul className="space-y-3 flex-1">
            {PRO_HEADLINE_FEATURES.map(f => (
              <li key={f} className="flex items-start gap-2.5 font-body text-[14px] text-deep-green font-medium">
                <span className="mt-0.5 flex-shrink-0"><CheckIcon green /></span>
                {f}
              </li>
            ))}

            <li>
              <button
                onClick={() => setShowAll(!showAll)}
                className="flex items-center gap-1.5 font-body text-[13px] text-sage
                           hover:text-deep-green transition-colors pt-1"
              >
                See all Pro features <ChevronIcon open={showAll} />
              </button>
            </li>

            {showAll && PRO_MORE_FEATURES.map(f => (
              <li key={f} className="flex items-start gap-2.5 font-body text-[14px] text-dark-text">
                <span className="text-sage mt-0.5 flex-shrink-0"><CheckIcon /></span>
                {f}
              </li>
            ))}
          </ul>

          <ProCTA />
        </div>
      </div>

      <p className="text-center font-body text-[13px] text-sage">
        Cancel any time before your trial ends and you will not be charged
      </p>
    </div>
  );
}
